/**
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; under version 2
 * of the License (non-upgradable).
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 *
 * Copyright (c) 2019-2023 (original work) Open Assessment Technologies SA
 *
 */

import router from '../core/router.js';
import logger from 'core/logger';

let container;

/**
 * The provided controller object will be extended with page controller functions.
 * Usage:
 *  import pageController from 'controller/page';
 *  const controller = pageController(controller, {
 *      foo() {
 *          // in controller functions, page controller functions are available
 *          this.logger('message');
 *      }
 *  });
 *
 * @param {Object} controller - controller object that should be extended
 * @returns {Object} controller object that is extended with page controller functions.
 */
export default controller =>
    Object.assign(controller, {
        /**
         * Getter of page container
         * @returns {Element} Page container element
         */
        get container() {
            return container || (container = document.querySelector('#page'));
        },

        /**
         * Getter of router
         * @returns {Router} Router instance
         */
        get router() {
            return router;
        },

        /**
         * Getter of logger
         * @returns {Logger} Scoped logger based on controller name
         */
        get logger() {
            return logger(controller.name);
        },

        /**
         * Common prepare function before controller starts
         */
        prepare() {
            this.container.classList.add(this.name);
        },

        /**
         * Common clean function after controller stopped
         */
        clean() {
            const pageContainer = this.container;
            pageContainer.classList.remove(this.name);
            pageContainer.innerHTML = '';
        }
    });
